import { useEffect, useState } from 'react';

import ErrorIcon from '@mui/icons-material/Error';
import GetAppRoundedIcon from '@mui/icons-material/GetAppRounded';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Link,
  MenuItem,
  Popover,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from 'react-query';
import { CustomTooltip } from 'src/components/CustomTooltip';
import { Permissions } from 'src/types/user';

import { useAuthContext } from '../../../context/AuthContextProvider';
import { getPlansWithPermission } from '../../../services/subscription';
import { buildNamesInline } from '../../../utils/string';

const MenuItemCustom = ({
  disabled,
  label,
  handleAction,
}: {
  disabled: boolean;
  label: string;
  handleAction: () => void;
}) => {
  return (
    <Tooltip
      disableFocusListener
      disableTouchListener={!disabled}
      disableHoverListener={!disabled}
      arrow
      title={'Faça o upgrade do seu plano para utilizar essa funcionalidade'}
      placement="top">
      <span>
        <MenuItem
          disabled={disabled}
          onClick={() => handleAction()}
          sx={(theme) => ({
            fontSize: '1rem',
            fontWeight: 500,
            padding: theme.spacing(2, 3),
          })}>
          {label}
        </MenuItem>
      </span>
    </Tooltip>
  );
};

export function DataExport({
  handlePrint,
  handleExport,
  permission,
  hasData = false,
  pdfEnabled = true,
  progress = 0,
  downloadUrl = '',
  isExporting = false,
  status,
  onReset,
  onDownload,
  showProgressOnExport = true,
}: {
  handlePrint: () => void;
  handleExport: () => void;
  permission: string;
  hasData: boolean;
  pdfEnabled?: boolean;
  progress?: number;
  downloadUrl?: string;
  isExporting?: boolean;
  status?: 'processing' | 'completed' | 'error';
  onReset?: () => void;
  onDownload?: () => Promise<void> | void;
  showProgressOnExport?: boolean;
}) {
  const { auth } = useAuthContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [progressText, setProgressText] = useState('');

  const hasPermission =
    !!auth.user?.subscription?.plan?.permissions[
      permission as keyof Permissions
    ];

  const { data: plansToUpgrade, isLoading: plansToUpgradeLoading } = useQuery(
    ['upgradedPlan', permission],
    () => getPlansWithPermission(permission),
    {
      enabled: !!permission && !hasPermission,
      retry: false,
    },
  );

  const isDisabled =
    !auth.isLoggedIn || !hasPermission || !hasData || plansToUpgradeLoading;

  useEffect(() => {
    if (status === 'error') {
      setProgressText('Erro ao processar exportação. Tente novamente.');
    } else if (progress > 0 && progress < 100) {
      setProgressText(`Gerando arquivo... ${progress}%`);
    } else if (progress === 100 && downloadUrl) {
      setProgressText('Documento disponível para download');
    } else if (progress === 100 && !downloadUrl) {
      setProgressText('Processamento concluído');
    }
  }, [progress, downloadUrl, status]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleExportWithProgress = () => {
    if (showProgressOnExport) {
      setSnackbarOpen(true);
    }
    handleExport();
  };

  const handleTryAgain = () => {
    if (onReset) {
      onReset();
    }
    setSnackbarOpen(true);
    handleExport();
  };

  const handleDownload = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!onDownload) {
      return;
    }

    event.preventDefault();

    try {
      await onDownload();
    } catch {
      setProgressText('Erro ao baixar o CSV. Tente novamente.');
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const canCloseSnackbar = progress === 100 || status === 'error';

  const getAlertSeverity = () => {
    if (status === 'error') return 'error';
    if (progress === 100) return 'success';
    return 'info';
  };

  const getAlertIcon = () => {
    if (status === 'error') return <ErrorIcon />;
    return <GetAppRoundedIcon color="primary" />;
  };

  return (
    <>
      <CustomTooltip
        title={
          !hasData
            ? ''
            : !auth.isLoggedIn || !hasPermission
              ? `Recurso disponível apenas para o(s) plano(s) ${buildNamesInline(plansToUpgrade?.data)}`
              : ''
        }
        placement="top"
        arrow>
        <span aria-roledescription={permission}>
          <Button
            variant="text"
            onClick={handleClick}
            sx={{
              '@media print': {
                display: 'none',
              },
              '& > *:first-child': {
                fontSize: '30px',
              },
            }}
            startIcon={
              <GetAppRoundedIcon color={isDisabled ? 'disabled' : 'primary'} />
            }
            disabled={isDisabled || isExporting}>
            <Typography
              sx={{
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: isDisabled ? '#00000042' : '#828282',
              }}>
              Exportar
            </Typography>
          </Button>
        </span>
      </CustomTooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}>
        <Box sx={{ width: 160 }}>
          <MenuItemCustom
            disabled={!hasPermission}
            label="CSV"
            handleAction={() => {
              setAnchorEl(null);
              handleExportWithProgress();
            }}
          />
          {pdfEnabled && (
            <MenuItemCustom
              disabled={!hasPermission}
              label="PDF"
              handleAction={() => {
                setAnchorEl(null);
                handlePrint();
              }}
            />
          )}
        </Box>
      </Popover>

      <Snackbar
        open={snackbarOpen}
        onClose={canCloseSnackbar ? handleSnackbarClose : undefined}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={
          progress === 100 && downloadUrl
            ? 8000
            : status === 'error'
              ? 10000
              : null
        }>
        <Alert
          onClose={canCloseSnackbar ? handleSnackbarClose : undefined}
          severity={getAlertSeverity()}
          icon={getAlertIcon()}
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            border: `1px solid ${
              status === 'error'
                ? theme.palette.error.main
                : theme.palette.primary.main
            }`,
            width: '100%',
            minWidth: 280,
            backgroundColor: 'white',
            color:
              status === 'error'
                ? theme.palette.error.main
                : theme.palette.secondary.main,
            '& .MuiAlert-icon': {
              color:
                status === 'error'
                  ? theme.palette.error.main
                  : theme.palette.primary.main,
            },
          })}>
          <Box sx={{ width: '100%', minWidth: 240 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {progressText}
            </Typography>
            {progress < 100 && status !== 'error' && (
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ width: '100%', mb: 1 }}
              />
            )}
            {progress === 100 && downloadUrl && (
              <Link
                href={downloadUrl}
                onClick={handleDownload}
                download
                underline="hover"
                sx={{ fontWeight: 600 }}>
                Clique aqui para baixar
              </Link>
            )}
            {status === 'error' && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleTryAgain}
                sx={{
                  mt: 1,
                  borderColor: 'error.main',
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.main',
                    color: 'white',
                  },
                }}>
                Tentar novamente
              </Button>
            )}
          </Box>
        </Alert>
      </Snackbar>
    </>
  );
}
